import { useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { AlertCircle, CheckCircle, Loader2, Sparkles, X } from 'lucide-react'

import { billingApi } from '@/lib/api/billing'
import { Button } from '@/components/ui/Controls'
import { useAuth } from '@/features/auth/useAuth'
import type { CreditPackage } from './plans'

function loadRazorpayScript(): Promise<boolean> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') return resolve(false)
    if ((window as any).Razorpay) return resolve(true)
    const script = document.createElement('script')
    script.src = 'https://checkout.razorpay.com/v1/checkout.js'
    script.async = true
    script.onload = () => resolve(true)
    script.onerror = () => resolve(false)
    document.body.appendChild(script)
  })
}

export interface CheckoutOrderInfo {
  order_id: string
  amount_paise: number
  currency: string
  credits: number
  package_id: string
  package_name: string
  key_id: string
  user_name?: string | null
  user_email?: string | null
}

export function useRazorpayCheckout() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [mockOrder, setMockOrder] = useState<CheckoutOrderInfo | null>(null)

  const initiateCheckout = useCallback(
    async (pack: CreditPackage) => {
      setError(null)
      setSuccess(null)

      if (!user) {
        navigate({ to: '/signin' })
        return
      }

      setLoading(true)
      try {
        const order = await billingApi.createOrder(pack.id)

        // Check if Razorpay keys are placeholder/mock
        const isMock =
          !order.key_id ||
          order.key_id.startsWith('rzp_test_placeholder') ||
          order.order_id.startsWith('order_mock_')

        if (isMock) {
          // Open mock simulation modal
          setMockOrder(order)
          setLoading(false)
          return
        }

        // Real Razorpay checkout flow
        const scriptLoaded = await loadRazorpayScript()
        if (!scriptLoaded) {
          throw new Error('Could not load Razorpay checkout script. Check your internet connection.')
        }

        const RazorpayClass = (window as any).Razorpay
        const rzp = new RazorpayClass({
          key: order.key_id,
          amount: order.amount_paise,
          currency: order.currency,
          name: 'RepoPilot',
          description: `${order.package_name} pack (${order.credits} credits)`,
          order_id: order.order_id,
          prefill: {
            name: order.user_name || undefined,
            email: order.user_email || undefined,
          },
          theme: {
            color: '#10b981',
          },
          handler: async function (response: {
            razorpay_payment_id: string
            razorpay_order_id: string
            razorpay_signature: string
          }) {
            setLoading(true)
            try {
              const res = await billingApi.verifyPayment(response)
              await queryClient.invalidateQueries({ queryKey: ['billing'] })
              setSuccess(`Successfully added ${res.credits_granted} credits to your account!`)
            } catch (err: any) {
              setError(err?.message || 'Payment verification failed.')
            } finally {
              setLoading(false)
            }
          },
          modal: {
            ondismiss: function () {
              setLoading(false)
            },
          },
        })

        rzp.on('payment.failed', function (resp: any) {
          setError(resp?.error?.description || 'Payment failed.')
          setLoading(false)
        })

        rzp.open()
      } catch (err: any) {
        setError(err?.message || 'Failed to initialize payment order.')
        setLoading(false)
      }
    },
    [user, navigate, queryClient],
  )

  const handleSimulateMockPayment = useCallback(
    async (simulateSuccess: boolean) => {
      if (!mockOrder) return

      setLoading(true)
      setError(null)
      try {
        if (!simulateSuccess) {
          setError('Payment was simulated as failed/cancelled.')
          setMockOrder(null)
          return
        }

        const res = await billingApi.verifyPayment({
          razorpay_order_id: mockOrder.order_id,
          razorpay_payment_id: `pay_mock_${Date.now()}`,
          razorpay_signature: 'mock_signature',
        })

        await queryClient.invalidateQueries({ queryKey: ['billing'] })
        setSuccess(`Successfully credited ${res.credits_granted} credits to your account!`)
        setMockOrder(null)
      } catch (err: any) {
        setError(err?.message || 'Failed to complete mock verification.')
      } finally {
        setLoading(false)
      }
    },
    [mockOrder, queryClient],
  )

  const dismissModal = useCallback(() => {
    setMockOrder(null)
  }, [])

  return {
    initiateCheckout,
    loading,
    error,
    success,
    mockOrder,
    handleSimulateMockPayment,
    dismissModal,
  }
}

export function RazorpayMockModal({
  order,
  loading,
  onSimulate,
  onClose,
}: {
  order: CheckoutOrderInfo
  loading: boolean
  onSimulate: (success: boolean) => void
  onClose: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="relative w-full max-w-md rounded-2xl border border-line bg-surface p-6 shadow-2xl">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-lg p-1 text-ink-muted hover:bg-hover hover:text-ink"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-ink">Razorpay Checkout</h3>
            <p className="text-xs text-ink-muted">Test / Mock Simulation Mode</p>
          </div>
        </div>

        <div className="mt-5 rounded-xl border border-line/60 bg-raised p-4 space-y-2 text-sm">
          <div className="flex justify-between text-ink-dim">
            <span>Package:</span>
            <span className="font-medium text-ink">{order.package_name}</span>
          </div>
          <div className="flex justify-between text-ink-dim">
            <span>Credits:</span>
            <span className="font-semibold text-primary">+{order.credits} Credits</span>
          </div>
          <div className="flex justify-between text-ink-dim">
            <span>Amount:</span>
            <span className="font-semibold text-ink">₹{order.amount_paise / 100}</span>
          </div>
          <div className="flex justify-between text-ink-dim text-xs">
            <span>Order ID:</span>
            <span className="font-mono text-ink-muted truncate max-w-[200px]">{order.order_id}</span>
          </div>
        </div>

        <p className="mt-4 text-xs text-ink-muted">
          Your Razorpay credentials are currently in test/placeholder mode. Once you add your live
          Razorpay Key ID and Secret to <code className="text-primary font-mono">.env</code>, this will
          open the official Razorpay Checkout popup automatically.
        </p>

        <div className="mt-6 flex flex-col gap-2">
          <Button
            variant="primary"
            disabled={loading}
            onClick={() => onSimulate(true)}
            className="w-full justify-center"
            icon={loading ? Loader2 : CheckCircle}
          >
            {loading ? 'Processing...' : 'Simulate Successful Payment'}
          </Button>
          <Button
            variant="ghost"
            disabled={loading}
            onClick={() => onSimulate(false)}
            className="w-full justify-center text-fail hover:text-fail"
          >
            Simulate Payment Failure / Cancel
          </Button>
        </div>
      </div>
    </div>
  )
}

export function CheckoutStatusToast({
  error,
  success,
  onDismiss,
}: {
  error: string | null
  success: string | null
  onDismiss?: () => void
}) {
  if (!error && !success) return null

  return (
    <div
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-xl border p-4 shadow-xl text-sm max-w-md ${
        error
          ? 'border-fail/30 bg-fail-wash text-fail'
          : 'border-ok/30 bg-ok-wash text-ok'
      }`}
    >
      {error ? (
        <AlertCircle className="h-5 w-5 shrink-0" />
      ) : (
        <CheckCircle className="h-5 w-5 shrink-0 text-ok" />
      )}
      <span className="flex-1 font-medium">{error || success}</span>
      {onDismiss ? (
        <button
          onClick={onDismiss}
          className="rounded p-1 text-ink-muted hover:text-ink"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  )
}
